import { Badge, Box, IconButton, Input, Spinner, Table } from '@chakra-ui/react';
import { FiCheck, FiTrash2, FiX } from 'react-icons/fi';
import { getSubjectCellClassName } from './teacherUtils';

export type TeacherTableRowProps = {
  teacher: any;
  index: number;
  subjectColumns: string[];
  teacherSlotCounts: Map<string, number>;
  pendingSubjectUpdate: string | null;
  pendingTeacherDelete: string | null;
  pendingTeacherCreate: boolean;
  pendingTeacherRename: string | null;
  editingTeacherName: string | null;
  editingTeacherValue: string;
  setEditingTeacherValue: (value: string) => void;
  handleSubjectToggle: (teacherName: string, code: string, hasSubject: boolean) => Promise<void> | void;
  handleTeacherDelete: (teacherName: string) => void;
  handleTeacherRenameStart: (teacherName: string) => void;
  handleTeacherRenameSubmit: (teacherName: string, teacher: any) => Promise<void> | void;
  onTeacherRenameCancel: () => void;
};

export const TeacherTableRow = ({
  teacher,
  index,
  subjectColumns,
  teacherSlotCounts,
  pendingSubjectUpdate,
  pendingTeacherDelete,
  pendingTeacherCreate,
  pendingTeacherRename,
  editingTeacherName,
  editingTeacherValue,
  setEditingTeacherValue,
  handleSubjectToggle,
  handleTeacherDelete,
  handleTeacherRenameStart,
  handleTeacherRenameSubmit,
  onTeacherRenameCancel,
}: TeacherTableRowProps) => {
  const teacherName = teacher?.name ?? '';
  const teacherSubjects = new Set(teacher?.subjects ?? []);
  const slotCount = teacherSlotCounts.get(teacherName) ?? 0;
  const isDeleting = pendingTeacherDelete === teacherName;
  const isRenaming = editingTeacherName === teacherName;
  const isRenamePending = pendingTeacherRename === teacherName;

  return (
    <Table.Row key={teacherName || index} className="teacher-row">
      <Table.Cell>
        <Box display="flex" alignItems="center" gap={2}>
          <Box className="teacher-row-delete" display="flex" alignItems="center" justifyContent="center" w={6} minW={6}>
            <IconButton
              aria-label={`Usuń korepetytora ${teacherName}`}
              size="2xs"
              variant="ghost"
              colorPalette="red"
              type="button"
              disabled={!teacherName || isDeleting || !!pendingSubjectUpdate || pendingTeacherCreate || !!pendingTeacherRename}
              onClick={(event) => {
                event.stopPropagation();
                handleTeacherDelete(teacherName);
              }}
            >
              {isDeleting ? <Spinner size="xs" /> : <FiTrash2 size={14} />}
            </IconButton>
          </Box>
          {isRenaming ? (
            <Box display="flex" alignItems="center" gap={2} flex="1" minW={0}>
              <Input
                value={editingTeacherValue}
                variant="flushed"
                onChange={(event) => setEditingTeacherValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleTeacherRenameSubmit(teacherName, teacher);
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    onTeacherRenameCancel();
                  }
                }}
                placeholder="Nazwa korepetytora"
                autoFocus
              />
              <IconButton
                aria-label={`Zapisz nazwę korepetytora ${teacherName}`}
                size="2xs"
                variant="ghost"
                type="button"
                disabled={isRenamePending || !editingTeacherValue.trim()}
                onClick={() => handleTeacherRenameSubmit(teacherName, teacher)}
              >
                {isRenamePending ? <Spinner size="xs" /> : <FiCheck size={14} />}
              </IconButton>
              <IconButton
                aria-label={`Anuluj zmianę nazwy korepetytora ${teacherName}`}
                size="2xs"
                variant="ghost"
                type="button"
                onClick={onTeacherRenameCancel}
              >
                <FiX size={14} />
              </IconButton>
            </Box>
          ) : (
            <Box
              as="button"
              flex="1"
              minW={0}
              onClick={() => handleTeacherRenameStart(teacherName)}
              textAlign="left"
              bg="transparent"
              border="none"
              p={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              fontWeight="medium"
            >
              {teacherName || '-'}
            </Box>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell textAlign="center">
        <Badge
          variant={slotCount > 0 ? 'solid' : 'subtle'}
          colorPalette={slotCount > 0 ? 'black' : 'gray'}
          size="sm"
        >
          {slotCount}
        </Badge>
      </Table.Cell>
      {subjectColumns.map((code) => {
        const hasSubject = teacherSubjects.has(code);
        const isUpdating = pendingSubjectUpdate === `${teacherName}::${code}`;
        const cellClass = getSubjectCellClassName(code);

        return (
          <Table.Cell
            key={`${teacherName || index}-${code}`}
            className={cellClass}
            textAlign="center"
            cursor="pointer"
            px={0}
            py={0}
            onClick={() => !isUpdating && handleSubjectToggle(teacherName, code, hasSubject)}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              h={7}
              w={7}
              mx="auto"
              borderRadius="sm"
              bg="transparent"
              color={hasSubject ? 'orange' : 'transparent'}
              transition="all 0.15s ease"
            >
              {isUpdating ? <Spinner size="sm" color={!hasSubject ? 'gray.400' : 'black'} /> : <FiCheck size={24} />}
            </Box>
          </Table.Cell>
        );
      })}
    </Table.Row>
  );
};
